import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Registration } from '../../../types/registration';

interface AccommodationData {
  placeCode: string;
  placeName: string;
  priceCode: string;
  status: string;
  [key: string]: any;
}

interface AccommodationAssignmentCardProps {
  registration: Registration;
  campSettings: any;
  onRegistrationUpdate: (updatedRegistration: Registration) => void;
}

const AccommodationAssignmentCard: React.FC<AccommodationAssignmentCardProps> = ({
  registration,
  campSettings,
  onRegistrationUpdate
}) => {
  const [accommodationAssignment, setAccommodationAssignment] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const handleAssignAccommodation = async () => {
    if (!registration || !accommodationAssignment.trim()) return;

    setIsAssigning(true);
    try {
      // First, validate the accommodation in inventory
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const inventorySnap = await getDoc(inventoryRef);
      
      if (!inventorySnap.exists()) {
        alert('Accommodation inventory not found');
        return;
      }
      
      const inventoryData = inventorySnap.data();
      const placeCode = accommodationAssignment.trim();
      
      console.log('Searching for place code:', placeCode);
      console.log('Inventory data:', inventoryData);
      
      // Search through all accommodation types to find the placeCode
      let foundAccommodation: AccommodationData | null = null;
      const accommodationTypes = ['Apartment', 'Cottage', 'Fireside', 'LowerCourt', 'UpperCourt', 'dormA', 'dormB', 'privateCottage'];
      
      for (const type of accommodationTypes) {
        console.log(`Checking ${type}:`, inventoryData[type]);
        if (inventoryData[type] && Array.isArray(inventoryData[type])) {
          const accommodation = inventoryData[type].find((item: any) => {
            console.log(`Comparing ${item.placeCode} with ${placeCode}`);
            return item.placeCode && item.placeCode.toLowerCase() === placeCode.toLowerCase();
          }) as AccommodationData;
          if (accommodation) {
            console.log('Found accommodation:', accommodation);
            foundAccommodation = accommodation;
            break;
          }
        }
      }
      
      if (!foundAccommodation) {
        alert(`Accommodation with place code "${placeCode}" not found in inventory`);
        return;
      }
      
      // Warn based on inventory status only: anything not "available" requires confirmation.
      const accommodationStatus = String(foundAccommodation.status || '').trim().toLowerCase();
      if (accommodationStatus !== 'available') {
        let assignedToName = '';
        try {
          const registrationsSnap = await getDocs(collection(db, 'Registrations'));
          registrationsSnap.forEach((docSnap) => {
            if (assignedToName) return;
            const data = docSnap.data();
            const status = String(data.status || '').trim().toLowerCase();
            if (status === 'cancelled') return;

            const assignedCode = String(data.financial?.accommodationFees?.assignedAccommodation || '').trim().toLowerCase();
            if (!assignedCode || assignedCode !== placeCode.toLowerCase()) return;

            // Prefer another registration over current one if both exist.
            if (docSnap.id === registration.id) return;

            assignedToName = String(
              data.registrationInfo?.primaryCamperName ||
              `${data.firstName || ''} ${data.lastName || ''}`.trim()
            ).trim();
          });
        } catch (lookupError) {
          console.warn('Unable to look up current accommodation assignee:', lookupError);
        }

        const statusDisplay = String(foundAccommodation.status || '').trim() || 'occupied';
        const warningContext = assignedToName
          ? `assigned to "${assignedToName}"`
          : `currently marked "${statusDisplay}"`;

        const confirmReassign = window.confirm(
          `Accommodation "${placeCode}" is ${warningContext}. Do you want to continue to assign it to this camper?`
        );

        if (!confirmReassign) {
          setIsAssigning(false);
          return;
        }
      }
      
      // Calculate accommodation fee
      let accommodationFee = 0;
      let nights = 0;
      if (registration?.campDates?.arrivalDate && registration?.campDates?.departureDate) {
        const arrival = new Date(registration.campDates.arrivalDate);
        const departure = new Date(registration.campDates.departureDate);
        const timeDiff = departure.getTime() - arrival.getTime();
        nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
      }
      const isFullCamp = nights >= 5;
      
      if (campSettings?.rates?.accommodation && foundAccommodation!.priceCode) {
        const priceCode = String(foundAccommodation!.priceCode).toUpperCase();
        const dormRates = campSettings.rates.accommodation.dorm;

        // Dorm/Fireside fix: D1/D2 always come from the dorm rates bucket.
        if (priceCode.startsWith('D') && dormRates?.[priceCode]) {
          const rate = dormRates[priceCode];
          if (rate.weekly && rate.nightly) {
            accommodationFee = isFullCamp ? rate.weekly : (rate.nightly * nights);
          }
        } else {
          const accommodationTypes = ['apartments', 'campsite', 'cottages', 'fireside', 'lowerCourt', 'upperCourt', 'dormA', 'dormB', 'privateCottage'];
          
          for (const type of accommodationTypes) {
            if (campSettings.rates.accommodation[type] && campSettings.rates.accommodation[type][priceCode]) {
              const rate = campSettings.rates.accommodation[type][priceCode];
              if (rate.weekly && rate.nightly) {
                accommodationFee = isFullCamp ? rate.weekly : (rate.nightly * nights);
                break;
              }
            }
          }
        }
      }
      
      // Update the registration with the accommodation details and calculated fee
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        'financial.accommodationFees.assignedAccommodation': placeCode,
        'financial.accommodationFees.placeName': foundAccommodation!.placeName,
        'financial.accommodationFees.priceCode': foundAccommodation!.priceCode,
        'financial.accommodationFees.accommodationFee': accommodationFee,
        'financial.accommodationFees.numberOfNights': nights
      });
      
      // Update local state
      const updatedRegistration = {
        ...registration,
        financial: {
          ...registration.financial,
          accommodationFees: {
            ...registration.financial?.accommodationFees,
            assignedAccommodation: placeCode,
            placeName: foundAccommodation!.placeName,
            priceCode: foundAccommodation!.priceCode,
            accommodationFee: accommodationFee,
            numberOfNights: nights
          }
        }
      } as Registration;
      
      onRegistrationUpdate(updatedRegistration);
      setAccommodationAssignment('');
      alert(`Successfully assigned accommodation: ${foundAccommodation!.placeName} (${placeCode})`);
    } catch (error) {
      console.error('Error assigning accommodation:', error);
      alert('Failed to assign accommodation. Please try again.');
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-body p-3">
        <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
          Accommodation Assignment
        </h6>
        
        <div className="mb-3">
          <label className="form-label">Assignment</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="Enter accommodation assignment (e.g., c116)"
              value={accommodationAssignment}
              onChange={(e) => setAccommodationAssignment(e.target.value)}
              style={{ minWidth: '300px' }}
            />
            <Button 
              variant="primary" 
              size="lg"
              onClick={handleAssignAccommodation}
              disabled={!accommodationAssignment.trim() || isAssigning}
            >
              {isAssigning ? 'Assigning...' : 'Assign Accommodation'}
            </Button>
          </div>
        </div>
        
        {registration?.financial?.accommodationFees?.assignedAccommodation && (
          <div className="alert alert-success">
            <strong>Assigned Accommodation:</strong><br />
            <strong>Place Code:</strong> {registration.financial.accommodationFees.assignedAccommodation}<br />
            <strong>Place Name:</strong> {registration.financial.accommodationFees.placeName || 'N/A'}<br />
            <strong>Price Code:</strong> {registration.financial.accommodationFees.priceCode || 'N/A'}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccommodationAssignmentCard;

